import { ProjectTypes } from '@platform/types';
import { LngLatLike, Map, MapGeoJSONFeature, Point2D } from 'maplibre-gl';
import React from 'react';
import useDatasetLastPublishedState from '../../../../hooks/useDatasetLastPublishedState';
import { columnFormatter } from '../../../Datasets/Dataset/DatasetTable/utils';
import WorkingLayerTooltip, { TooltipContentItem } from '../WorkingLayerTooltip';
import WorkingLayerTooltipPositioner from '../WorkingLayerTooltipPositioner';
import useWorkingLayerTooltipData from './useWorkingLayerTooltipData';

interface Props {
  map: Map | null;
  workingLayer: ProjectTypes.UserUploadedWorkingLayer;
  features: MapGeoJSONFeature[];
  latLong: LngLatLike | null;
  enabled: boolean;
  onFeaturesUnderMouse: (wId: string, f: MapGeoJSONFeature[], latLng: LngLatLike | null) => void;
  activeFeature: MapGeoJSONFeature | null;
  featureNavi?: React.ReactNode;
  emptyPlaceholder: React.ReactNode;
  actions?: React.ReactNode;
  inEditMode: boolean;
  onClose?: () => void;
}

const UserUploadedWorkingLayerTooltipWrapper = ({
  map,
  onFeaturesUnderMouse,
  features,
  latLong,
  workingLayer,
  activeFeature,
  actions,
  onClose,
  emptyPlaceholder,
  enabled,
}: Props) => {
  const tooltip = workingLayer.tooltip as ProjectTypes.WorkingLayerTooltip;
  const { query: lastPublishedDataQuery } = useDatasetLastPublishedState({
    datasetId: workingLayer.metadata.datasetId,
  });
  const [featuresData, isLoading] = useWorkingLayerTooltipData({
    enabled,
    workingLayer,
    map,
    features,
    onFeaturesUnderMouse,
  });

  if (isLoading && !emptyPlaceholder) return null;

  const lastPublishedData = lastPublishedDataQuery.data;
  if (!activeFeature || !lastPublishedData) return null;
  const activeFeatureIndex = features.indexOf(activeFeature);

  if (activeFeatureIndex === -1) return null;
  if (!latLong) return null;

  const projectedAnchor =
    activeFeature.geometry.type === 'Point'
      ? (map?.project(activeFeature.geometry.coordinates as LngLatLike) as Point2D)
      : (map?.project(latLong) as Point2D);

  const activeFeatureData = featuresData[activeFeatureIndex];

  let title = activeFeatureData
    ? (activeFeatureData[workingLayer.metadata.identifiers.row.variableId] as string)
    : undefined;

  let content: TooltipContentItem[] = [];

  if (tooltip.enabled) {
    if (tooltip.title) {
      title = activeFeatureData?.[tooltip.title.variableId] as string;
    }

    content = tooltip.content.map((x) => {
      const index = lastPublishedData.columns.findIndex((a) => a.id === x.id);
      const format = lastPublishedData.columns[index].format;

      const value = format
        ? columnFormatter(activeFeatureData?.[x.variableId], format)
        : activeFeatureData?.[x.variableId];

      return {
        id: x.id,
        title: x.label ?? x.variableId,
        value: value?.toString() || 'N/A',
        comparedTo: undefined,
      };
    });
  }

  //  const featureId = activeFeatureData?.[workingLayer.metadata.identifiers.row.variableId] as string;

  // const handleFeatureSelectionToggle = () => {
  //   if (!activeFeature) return;
  //   if (!onFeatureSelectionChange) return;
  //
  //   const featureName = activeFeatureData[
  //     workingLayer.metadata.identifiers.name?.variableId ?? workingLayer.metadata.identifiers.row.variableId
  //   ] as string;
  //   onFeatureSelectionChange(workingLayer.id, featureId, featureName);
  // };
  //
  // const activeFeatureIsInSelection = workingLayer.featureSelection.find((f) => f.id === featureId);

  // TODO: is being repeated in SocialExplorerWorkingLayerTooltipWrapper
  // const selectionButton = onFeatureSelectionChange ? (
  //   <Buttons.Secondary
  //     onClick={handleFeatureSelectionToggle}
  //     className="bg-gray-200 text-gray-700 ring-0 hover:bg-gray-100"
  //   >
  //     {activeFeatureIsInSelection ? 'Deselect' : 'Add to selection'}
  //   </Buttons.Secondary>
  // ) : null;

  // const extendedActions = <>{actions}</>;

  return (
    <WorkingLayerTooltipPositioner anchorPoint={projectedAnchor} showAnchor={activeFeature.geometry.type !== 'Point'}>
      <WorkingLayerTooltip
        title={String(title)}
        heading={workingLayer.name}
        data={content}
        footer={actions}
        onClose={onClose}
        emptyPlaceholder={emptyPlaceholder}
      />
    </WorkingLayerTooltipPositioner>
  );
};

export default UserUploadedWorkingLayerTooltipWrapper;
